import { ReactNode } from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import { IColumnSelectionState, ITableColumn } from '../types';
import styles from './ColumnSelectionDialog.module.scss';
import { useColumnSelectionDialog } from './useColumnSelectionDialog';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import FontAwesomeIcon from 'src/components/Icon';
import NewBadge from 'src/components/NewBadge/NewBadge';

interface IColumnSelectionDialogProps {
  allColumns: ITableColumn[];
  selectedColumns: IColumnSelectionState[];
  onChange: (selectedColumns: IColumnSelectionState[]) => void;
  onClose: () => void;
  show?: boolean;
  note?: ReactNode;
}

export const ColumnSelectionDialog = ({
  allColumns,
  selectedColumns,
  onChange,
  onClose,
  show,
  note,
}: IColumnSelectionDialogProps) => {

  const {
    draggedAccessor,
    orderedColumns,
    selectedAccessors,
    setItemRef,
    toggleColumn,
    startDrag,
    hoverColumn,
    dropColumn,
    endDrag,
    resetColumnsToDefault,
  } = useColumnSelectionDialog({
    allColumns,
    selectedColumns,
    onChange,
  });

  return (
    <GenericDialogue
      isOpen={show}
      onClose={onClose}
      title={
        <>
          Adjust Columns for the Service Requests table{' '}
          <NewBadge className={styles['new-badge-spacing']} />
        </>
      }
    >
      {note && <div className={styles['column-selection-note']}>{note}</div>}
      <div className={styles['column-selection-list']}>
        {orderedColumns.map((column) => {
          const isChecked: boolean =
            selectedAccessors.has(column.accessor) ||
            !!column.columnSelectionDisabled;
          const columnLabel: string =
            column.columnSelectionLabel || column.label || column.accessor;

          return (
            <div
              key={column.accessor}
              ref={setItemRef(column.accessor)}
              className={styles['column-selection-item']}
              draggable
              onDragStart={() => startDrag(column.accessor)}
              onDragEnter={() => hoverColumn(column)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => dropColumn()}
              onDragEnd={() => endDrag()}
              style={{
                opacity: draggedAccessor === column.accessor ? 0.5 : 1,
                userSelect: 'none',
              }}
            >
              <Checkbox
                checked={isChecked}
                value={isChecked ? column.accessor : ''}
                label={columnLabel}
                onChange={() => toggleColumn(column)}
                disabled={column.columnSelectionDisabled}
              />
              <div style={{ flex: 1 }} />
              <FontAwesomeIcon
                glyph="grip-lines"
                weight="solid"
                className={styles.grabber}
              />
            </div>
          );
        })}
      </div>
      <div className={styles['column-selection-actions']}>
        <Button size="small" outlined onClick={() => resetColumnsToDefault()}>
          Reset to Default
        </Button>
        <Button size="small" color="teal" onClick={onClose}>
          Done
        </Button>
      </div>
    </GenericDialogue>
  );
};
